import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, Table } from 'react-bootstrap';
import Utils from '../util/Utils.jsx';
import { getDatasetInfo, mergeDatasetInfoExtras } from '../offline-features/getDatasetInfo.js';
import { getDatasets } from '../offline-features/getDatasets.js';
import { useStateValue } from '../context/state.jsx';
import { openAlert } from '../context/alert.js';
import { setDatasetInfo, setDatasets } from '../context/datasets.js';
import ScheduleMatchReview from './ScheduleMatchReview.jsx';
import {
	duplicateFilenameIds,
	formatVersionAccessibleName,
	formatVersionLabel,
	formatVersionOptionLabel,
	formatVersionStamp
} from '../util/scheduleVersionIdentity.js';

const formatLabel = formatVersionLabel;

const formatStamp = formatVersionStamp;

const formatSource = sourceFormat => (
	sourceFormat === 'mspdi' ? 'MS Project XML' : 'P6 XER'
);

const matchSummary = preview => {
	if (!preview) {
		return '';
	}
	const pairs = preview.matchedPairs != null ? Number(preview.matchedPairs) : Number(preview.matched) || 0;
	const total = Number(preview.displayCount) || 0;
	const percent = Number.isFinite(Number(preview.percent)) ? Number(preview.percent) : 0;
	const extras = [];
	if (preview.newScope) {
		extras.push(`${preview.newScope} new scope`);
	}
	if (preview.dropped) {
		extras.push(`${preview.dropped} dropped`);
	}
	return `${pairs} of ${total} activities matched (${percent}%)${extras.length ? ` · ${extras.join(' · ')}` : ''}`;
};

const versionOptionLabel = (version, duplicate) => formatVersionOptionLabel(version, { duplicate });

const applyLoadedDataset = (dispatch, datasetId, result, datasets) => {
	if (!result || !result.success) {
		return;
	}
	dispatch(setDatasetInfo(
		datasetId,
		result.wbsTree,
		result.activities,
		result.activitiesMap,
		result.activityCodesRelativesMap,
		Array.isArray(result.activityCodesTree) ? null : result.activityCodesTree,
		result.activityCodesTreeSequence,
		result.layouts,
		result.Id || null,
		result.ViewType || 'WBS List',
		result.GroupBy || null,
		result.GroupByActivityCode || null,
		result.MapGroupBy || null
	));
	if (datasets && datasets[datasetId]) {
		dispatch(setDatasets({
			...datasets,
			[datasetId]: mergeDatasetInfoExtras(datasets[datasetId], result)
		}));
	}
};

const ScheduleVersionList = ({ datasetId, datasetName }) => {
	const [{ currentDataset, projectActivities, datasets }, dispatch] = useStateValue();
	const [versions, setVersions] = useState([]);
	const [dirtyCount, setDirtyCount] = useState(0);
	const [label, setLabel] = useState('');
	const [busy, setBusy] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [matchByVersion, setMatchByVersion] = useState({});
	const [pending, setPending] = useState(null);
	const [confirmSame, setConfirmSame] = useState(false);
	const [reviewByVersion, setReviewByVersion] = useState({});
	const [reviewVersionId, setReviewVersionId] = useState(0);
	const fileInputRef = useRef(null);

	const rememberMatch = (versionId, preview) => {
		if (!versionId || !preview) {
			return;
		}
		setMatchByVersion(prev => ({
			...prev,
			[String(versionId)]: preview
		}));
	};

	const rememberReview = (versionId, review) => {
		if (!versionId || !review) {
			return;
		}
		setReviewByVersion(prev => ({
			...prev,
			[String(versionId)]: review
		}));
	};

	const applyPayload = (payload, versionId) => {
		if (payload && Array.isArray(payload.versions)) {
			setVersions(payload.versions);
			payload.versions.forEach(version => {
				if (version && version.matchPreview) {
					rememberMatch(version.id, version.matchPreview);
				}
			});
		}
		if (payload && typeof payload.dirtyCount === 'number') {
			setDirtyCount(payload.dirtyCount);
		}
		const previewId = versionId || (payload && payload.version && payload.version.id) || (payload && payload.versionId);
		if (payload && payload.matchPreview) {
			rememberMatch(previewId, payload.matchPreview);
		}
		if (payload && payload.matchReview) {
			rememberReview(previewId || payload.toVersionId || payload.reviewVersionId, payload.matchReview);
		}
		if (payload && payload.reviewVersionId && !reviewVersionId) {
			setReviewVersionId(Number(payload.reviewVersionId));
		}
	};

	const loadVersions = (nextReviewId) => {
		const payload = { dataset: String(datasetId) };
		if (nextReviewId || reviewVersionId) {
			payload.reviewVersionId = String(nextReviewId || reviewVersionId);
		}
		Utils.ajaxAsync('getScheduleVersions', payload, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (result && result.success) {
					setLoadError('');
					applyPayload(result);
				} else {
					setLoadError((result && result.error) || 'Unable to load schedule versions.');
				}
			})
			.catch(() => {
				setLoadError('Unable to load schedule versions.');
			});
	};

	useEffect(() => {
		loadVersions();
	}, [datasetId, datasetName]);

	useEffect(() => {
		if (!versions.length) {
			return;
		}
		if (!reviewVersionId) {
			const baseline = versions.find(item => item.isBaseline && !item.isDisplayed);
			const other = versions.find(item => !item.isDisplayed);
			const next = baseline || other;
			if (next) {
				setReviewVersionId(next.id);
			}
			return;
		}
		if (!reviewByVersion[String(reviewVersionId)]) {
			loadReview(reviewVersionId);
		}
	}, [datasetId, versions, reviewVersionId]);

	const clientHasChanged = () => {
		if (String(currentDataset) !== String(datasetId) || !Array.isArray(projectActivities)) {
			return dirtyCount > 0;
		}
		return projectActivities.some(activity => activity && activity.hasChanged) || dirtyCount > 0;
	};

	const confirmSwitch = (message, onConfirm) => {
		dispatch(openAlert('Switch displayed schedule?', message, [{
			bsStyle: 'danger',
			text: 'Switch and discard updates',
			action: onConfirm
		}, {
			text: 'Cancel',
			action: () => true
		}]));
	};

	const refreshOpenProject = () => {
		getDatasets(dispatch).then(result => {
			if (result.success && result.datasets) {
				dispatch(setDatasets(result.datasets));
			}
		}).catch(() => {});

		if (String(currentDataset) !== String(datasetId)) {
			return;
		}
		getDatasetInfo(dispatch, datasetId).then(result => {
			applyLoadedDataset(dispatch, datasetId, result, datasets);
		}).catch(() => {});
	};

	const clearPending = () => {
		setPending(null);
		setConfirmSame(false);
	};

	const showMatchPending = (type, versionId, preview) => {
		rememberMatch(versionId, preview);
		setPending({ type, versionId, matchPreview: preview });
		setConfirmSame(!preview.requiresConfirm);
		if (versionId) {
			setReviewVersionId(Number(versionId));
			loadReview(versionId);
		}
	};

	const loadReview = versionId => {
		if (!versionId) {
			return;
		}
		Utils.ajaxAsync('previewScheduleMatch', {
			dataset: String(datasetId),
			versionId: String(versionId)
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (result && result.success) {
					if (result.matchPreview) {
						rememberMatch(versionId, result.matchPreview);
					}
					if (result.matchReview) {
						rememberReview(versionId, result.matchReview);
					}
				}
			})
			.catch(() => {});
	};

	const previewFor = version => {
		if (!version) {
			return null;
		}
		return matchByVersion[String(version.id)] || version.matchPreview || null;
	};

	const setDisplay = (versionId, confirmed = false, confirmSameSchedule = false) => {
		const version = versions.find(item => item.id === versionId);
		if (!version || version.isDisplayed || busy) {
			return;
		}

		const preview = previewFor(version);
		if (preview && preview.requiresConfirm && !confirmSameSchedule) {
			showMatchPending('display', versionId, preview);
			return;
		}

		if (!confirmed && clientHasChanged()) {
			const countLabel = dirtyCount > 0 ? ` (${dirtyCount})` : '';
			confirmSwitch(
				`This project has field updates${countLabel} on the currently displayed schedule. Switching Display re-imports the selected file and will discard those updates. The stored files are kept.`,
				() => setDisplay(versionId, true, confirmSameSchedule)
			);
			return;
		}

		setBusy(true);
		Utils.ajaxAsync('setScheduleDisplay', {
			dataset: String(datasetId),
			versionId: String(versionId),
			confirmPurgeChanges: confirmed ? '1' : '0',
			confirmSameSchedule: confirmSameSchedule ? '1' : '0'
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (result && result.needsConfirm) {
					confirmSwitch(
						result.error || 'Switching Display will discard field updates on the current schedule.',
						() => setDisplay(versionId, true, confirmSameSchedule)
					);
					return;
				}
				if (result && result.needsMatchConfirm && result.matchPreview) {
					showMatchPending('display', versionId, result.matchPreview);
					return;
				}
				if (!result || !result.success) {
					dispatch(openAlert('Display switch failed', (result && result.error) || 'Unable to load that schedule version.'));
					return;
				}
				applyPayload(result, versionId);
				clearPending();
				refreshOpenProject();
			})
			.catch(() => {
				dispatch(openAlert('Display switch failed', 'Unable to load that schedule version.'));
			})
			.finally(() => {
				setBusy(false);
			});
	};

	const setBaseline = (versionId, checked, confirmSameSchedule = false) => {
		if (busy) {
			return;
		}
		const nextId = checked ? versionId : 0;
		if (!checked) {
			clearPending();
		} else {
			const version = versions.find(item => item.id === versionId);
			const preview = previewFor(version);
			if (preview && preview.requiresConfirm && !confirmSameSchedule) {
				showMatchPending('baseline', versionId, preview);
				return;
			}
		}
		setBusy(true);
		Utils.ajaxAsync('setScheduleBaseline', {
			dataset: String(datasetId),
			versionId: String(nextId),
			confirmSameSchedule: confirmSameSchedule ? '1' : '0'
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (result && result.needsMatchConfirm && result.matchPreview) {
					showMatchPending('baseline', versionId, result.matchPreview);
					return;
				}
				if (!result || !result.success) {
					dispatch(openAlert('Baseline update failed', (result && result.error) || 'Unable to update the baseline.'));
					return;
				}
				applyPayload(result, checked ? versionId : null);
				clearPending();
				refreshOpenProject();
			})
			.catch(() => {
				dispatch(openAlert('Baseline update failed', 'Unable to update the baseline.'));
			})
			.finally(() => {
				setBusy(false);
			});
	};

	const setExportSkeleton = versionId => {
		if (busy) {
			return;
		}
		const version = versions.find(item => item.id === versionId);
		if (!version || version.sourceFormat !== 'xer') {
			dispatch(openAlert('Export skeleton', 'Only a P6 .xer file can be the export skeleton.'));
			return;
		}
		setBusy(true);
		Utils.ajaxAsync('setScheduleExportSkeleton', {
			dataset: String(datasetId),
			versionId: String(versionId)
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (!result || !result.success) {
					dispatch(openAlert('Export skeleton failed', (result && result.error) || 'Unable to mark that P6 file as the export skeleton.'));
					return;
				}
				applyPayload(result, versionId);
				refreshOpenProject();
			})
			.catch(() => {
				dispatch(openAlert('Export skeleton failed', 'Unable to mark that P6 file as the export skeleton.'));
			})
			.finally(() => {
				setBusy(false);
			});
	};

	const applyPending = () => {
		if (!pending || busy) {
			return;
		}
		if (pending.matchPreview && pending.matchPreview.requiresConfirm && !confirmSame) {
			return;
		}
		if (pending.type === 'display') {
			setDisplay(pending.versionId, clientHasChanged(), confirmSame);
			return;
		}
		setBaseline(pending.versionId, true, confirmSame);
	};

	const onPickFile = event => {
		const file = event.target.files && event.target.files[0];
		event.target.value = '';
		if (!file) {
			return;
		}
		if (/\.mpp$/i.test(file.name)) {
			dispatch(openAlert(
				'Save as XML first',
				'Binary MS Project files (.mpp) are not supported. In MS Project choose File → Save As and pick XML (*.xml), then upload that file.'
			));
			return;
		}

		const formData = new FormData();
		formData.append('schedule', file);
		formData.append('dataset', datasetId);
		if (label.trim()) {
			formData.append('label', label.trim());
		}

		setBusy(true);
		Utils.ajaxAsync('uploadScheduleVersion', formData, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (!result || !result.success) {
					dispatch(openAlert('Upload failed', (result && result.error) || 'Unable to store that schedule file.'));
					return;
				}
				setLabel('');
				applyPayload(result, result.version && result.version.id);
				if (result.matchPreview && result.version && result.version.id) {
					setPending({
						type: 'upload',
						versionId: result.version.id,
						matchPreview: result.matchPreview
					});
					setConfirmSame(!result.matchPreview.requiresConfirm);
				}
			})
			.catch(() => {
				dispatch(openAlert('Upload failed', 'Unable to store that schedule file.'));
			})
			.finally(() => {
				setBusy(false);
			});
	};

	const pendingVersion = pending
		? versions.find(item => item.id === pending.versionId)
		: null;
	const pendingPreview = pending ? pending.matchPreview : null;
	const anyNeedsConfirm = versions.some(version => {
		if (version.isDisplayed) {
			return false;
		}
		const preview = previewFor(version);
		return !!(preview && preview.requiresConfirm);
	});
	const duplicateIds = duplicateFilenameIds(versions);

	return (
		<div className="scheduleVersionRepo">
			<div className="scheduleVersionRepoHeader">
				<div>
					<h4>Schedule versions</h4>
					<p>
						A project may keep a mix of MS Project XML and P6 XER weekly files.
						Display loads the selected schedule file as the visible snapshot.
						Baseline paints a second grey Gantt under the current bars in List View.
						Mark one .xer as the export skeleton so incremental XER writes progress onto those P6 task IDs.
					</p>
				</div>
			</div>
			{loadError ? <div className="setupInlineAlert setupInlineAlert-error">{loadError}</div> : null}
			{versions.length
				? (
					<Table condensed className="scheduleVersionTable">
						<thead>
							<tr>
								<th>Label</th>
								<th>Schedule file</th>
								<th>Uploaded</th>
								<th>Format</th>
								<th>Match</th>
								<th>Display</th>
								<th>Baseline</th>
								<th>Export skeleton</th>
							</tr>
						</thead>
						<tbody>
							{versions.map(version => {
								const preview = matchByVersion[String(version.id)];
								const matchClass = preview
									? (preview.sameSourceFormat === false
										? 'scheduleVersionMatch isDiffFormat'
										: (preview.requiresConfirm ? 'scheduleVersionMatch isLow' : 'scheduleVersionMatch'))
									: 'scheduleVersionMatch';
								const isDuplicate = !!duplicateIds[String(version.id)];
								return (
									<tr key={version.id} className={version.isDisplayed ? 'isDisplayed' : undefined}>
										<td>
											{formatLabel(version)}
											<div className="scheduleVersionIdentity">
												{formatStamp(version.uploadedAt)}
												{isDuplicate ? ` · #${version.id}` : ''}
											</div>
										</td>
										<td className="scheduleVersionFilename">{version.filename}</td>
										<td>{formatStamp(version.uploadedAt)}</td>
										<td>{formatSource(version.sourceFormat)}</td>
										<td className={matchClass}>
											{version.isDisplayed
												? 'Displayed'
												: (preview ? matchSummary(preview) : '—')}
										</td>
										<td className="scheduleVersionChoice">
											<input
												type="radio"
												name={`schedule-display-${datasetId}`}
												checked={!!version.isDisplayed}
												disabled={busy}
												onChange={() => setDisplay(version.id, false, confirmSame)}
												aria-label={formatVersionAccessibleName('Display', version, { duplicate: isDuplicate })} />
										</td>
										<td className="scheduleVersionChoice">
											<input
												type="checkbox"
												checked={!!version.isBaseline}
												disabled={busy}
												onChange={event => setBaseline(version.id, event.target.checked, confirmSame)}
												aria-label={formatVersionAccessibleName('Baseline', version, { duplicate: isDuplicate })} />
										</td>
										<td className="scheduleVersionChoice">
											{version.sourceFormat === 'xer'
												? (
													<input
														type="radio"
														name={`schedule-skeleton-${datasetId}`}
														checked={!!version.isExportSkeleton}
														disabled={busy}
														onChange={() => setExportSkeleton(version.id)}
														aria-label={formatVersionAccessibleName('Export skeleton', version, { duplicate: isDuplicate })} />
												)
												: <span className="scheduleVersionMuted">—</span>}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				)
				: (
					<div className="addProjectUsersEmpty">No stored schedule versions yet. Upload a weekly update below.</div>
				)}
			{versions.length
				? (
					<div className={anyNeedsConfirm ? 'scheduleVersionConfirm isRequired' : 'scheduleVersionConfirm'}>
						{pendingPreview
							? (
								<p>
									{pending.type === 'upload' ? 'Uploaded file vs displayed schedule: ' : null}
									{pending.type === 'display' ? 'Display switch match: ' : null}
									{pending.type === 'baseline' ? 'Baseline overlay match: ' : null}
									<strong>{matchSummary(pendingPreview)}</strong>
									{pendingPreview.unmatched
										? ` — ${pendingPreview.unmatched} displayed ${pendingPreview.unmatched === 1 ? 'activity' : 'activities'} unmatched.`
										: '.'}
									{' '}
									Selected {pendingVersion ? formatSource(pendingVersion.sourceFormat) : formatSource(pendingPreview.candidateFormat)}
									{' vs displayed '}
									{formatSource(pendingPreview.displayFormat)}
									{pendingPreview.sameSourceFormat ? '.' : ' — file types differ.'}
								</p>
							)
							: (
								<p>
									Match counts compare the displayed snapshot to each stored file using MSP Unique ID, MSP ID, task_code, then unique name. P6-only files also match task_id.
									Confirmation is identity only — SiteWalker does not merge MSP and P6 rows.
								</p>
							)}
						<label>
							<input
								type="checkbox"
								checked={confirmSame}
								disabled={busy}
								onChange={event => setConfirmSame(event.target.checked)} />
							<span>I confirm these files are the same schedule</span>
						</label>
						<p>
							Required when fewer than 90% of displayed activities match, or when XML and XER are paired.
							Pair leftovers or mark new scope / dropped below to raise the resolved rate. High match with the same file type can apply without checking.
						</p>
						{pending && pending.type !== 'upload'
							? (
								<div className="scheduleVersionConfirmActions">
									<Button
										bsStyle="primary"
										className="setupPrimaryBtn"
										disabled={busy || (pendingPreview && pendingPreview.requiresConfirm && !confirmSame)}
										onClick={applyPending}>
										{pending.type === 'display' ? 'Apply Display' : 'Apply Baseline'}
									</Button>
									<Button
										disabled={busy}
										onClick={clearPending}>
										Cancel
									</Button>
								</div>
							)
							: null}
					</div>
				)
				: null}
			{versions.some(version => !version.isDisplayed)
				? (
					<div>
						{versions.filter(item => !item.isDisplayed).length > 1
							? (
								<div className="scheduleMatchDropdown">
									<label htmlFor={`schedule-review-target-${datasetId}`}>Review against</label>
									<FormControl
										id={`schedule-review-target-${datasetId}`}
										componentClass="select"
										className="setupInput"
										disabled={busy}
										value={reviewVersionId || ''}
										onChange={event => {
											const nextId = Number(event.target.value);
											setReviewVersionId(nextId);
											loadReview(nextId);
										}}>
										{versions.filter(item => !item.isDisplayed).map(item => (
											<option key={item.id} value={item.id}>
												{versionOptionLabel(item, !!duplicateIds[String(item.id)])}
											</option>
										))}
									</FormControl>
								</div>
							)
							: null}
						<ScheduleMatchReview
							datasetId={datasetId}
							reviewVersion={versions.find(item => item.id === reviewVersionId) || null}
							preview={previewFor(versions.find(item => item.id === reviewVersionId)) || pendingPreview}
							review={reviewByVersion[String(reviewVersionId)] || null}
							busy={busy}
							dispatch={dispatch}
							onChanged={result => {
								applyPayload(result, reviewVersionId);
								if (result && result.matchReview) {
									rememberReview(reviewVersionId, result.matchReview);
								}
								refreshOpenProject();
							}} />
					</div>
				)
				: null}
			<div className="scheduleVersionUpload">
				<FormControl
					type="text"
					className="setupInput"
					placeholder="Label (optional) — e.g. WU05 or week of 17-Aug"
					value={label}
					disabled={busy}
					onChange={event => setLabel(event.target.value)} />
				<input
					ref={fileInputRef}
					type="file"
					accept=".xer,.xml,application/xml,text/xml"
					className="scheduleVersionFileInput"
					onChange={onPickFile} />
				<Button
					bsStyle="primary"
					className="setupPrimaryBtn"
					disabled={busy}
					onClick={() => {
						if (fileInputRef.current) {
							fileInputRef.current.click();
						}
					}}>
					{busy ? 'Working…' : 'Upload weekly update'}
				</Button>
			</div>
		</div>
	);
};

export default ScheduleVersionList;

ScheduleVersionList.propTypes = {
	datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	datasetName: PropTypes.string
};
